/**
 * Profile Questionnaire — pure update.
 *
 * Elm-ish: this function is pure. No API calls. No hooks. No I/O. It
 * returns `{ model, effects }`. The harness in
 * `useProfileQuestionnaireProgram.ts` runs the effects outside the
 * reducer.
 */

import { computeDirty } from './model';
import type {
  ProfileFormModel,
  ProfileForm,
  PhotoEntry,
} from './model';
import { MAX_SELECTED_SKILLS } from '@/api/skills';

/**
 * Convenience wrapper: `computeDirty` needs the four photo arrays
 * plus the skills arrays. Threading all six through every call site
 * is noisy; this helper reads them off the model.
 *
 * IMPORTANT: callers that have ALREADY constructed a new `form`
 * (e.g. inside `NameChanged`) must spread it back into a model
 * shape and pass that. The helper intentionally reads `model.form`,
 * so the model passed in must reflect the post-update state.
 */
function recomputeDirty(model: ProfileFormModel): boolean {
  return computeDirty(
    model.form,
    model.original,
    model.persistedPhotos,
    model.transientPhotos,
    model.selectedSkillIds,
    model.persistedSkillIds
  );
}
import type { ProfileQuestionnaireMsg } from './messages';
import { validateAllFields } from './validation';
import { goalToScenario } from './rolePresentation';
import {
  normalizeWorkSphere,
  normalizeEducationSphere,
  normalizeEducationType,
} from './profileNormalization';
import { consumeLifecycleFromServer } from './lifecycle';

/**
 * Profile questionnaire effects.
 *
 * The reducer is pure; effects run outside it via the harness.
 * Photo-related effects carry the localId (or photo UUID) and the
 * File reference so the harness can dispatch the API call. The
 * object-URL preview lifecycle lives in two effects:
 *   - CreateObjectURLPreview: allocate a preview URL via the
 *     injected port.
 *   - RevokeObjectURLPreview: release it.
 *
 * The legacy `RevokePhotoUrl` effect remains for backward
 * compatibility with the existing tests; it is dispatched by the
 * pre-existing `PhotoDeleteRequested` reducer when removing a
 * pending (non-durable) entry. New code should prefer
 * `RevokeObjectURLPreview` so tests can verify create/revoke
 * parity through the injected port seam.
 */
export type ProfileQuestionnaireEffect =
  | { readonly type: 'LoadExistingProfile' }
  | {
      readonly type: 'SaveProfile';
      readonly form: ProfileForm;
      readonly kind: 'create' | 'edit';
    }
  | { readonly type: 'ScheduleCompletionModalDismiss'; readonly durationMs: number }
  | { readonly type: 'RevokePhotoUrl'; readonly url: string }
  // B-2.B photo lifecycle:
  | {
      readonly type: 'UploadPhoto';
      readonly localId: string;
      readonly file: File;
    }
  | {
      readonly type: 'DeletePhoto';
      readonly photoID: string;
    }
  | {
      readonly type: 'RevokeObjectURLPreview';
      readonly url: string;
    }
  // B-2.C skills lifecycle:
  | { readonly type: 'LoadSkillsTaxonomy' }
  | { readonly type: 'LoadProfileSkills' }
  | {
      readonly type: 'SaveProfileSkills';
      readonly skillIds: readonly string[];
    };

export interface UpdateResult {
  readonly model: ProfileFormModel;
  readonly effects: readonly ProfileQuestionnaireEffect[];
}

/**
 * Convert API profile to internal `ProfileForm`. Maps backend `Goal`
 * -> internal `InternalScenario`. Drops unknown fields silently.
 *
 * The three B-2.A optional fields (work_sphere / education_sphere /
 * education_type) go through strict protocol normalization
 * (see `profileNormalization.ts`). Unknown values are dropped to
 * `undefined` rather than silently coerced to empty string — the
 * server remains authoritative, and the FE refuses to propagate
 * junk strings into form state.
 *
 * R1 fix: when normalization drops a field, the reason is returned
 * via `protocolViolations` so the caller can surface it to the user.
 * The reducer at `ExistingProfileLoaded` merges these into the
 * existing lifecycle `serverError`.
 */
export interface ProfileToFormResult {
  readonly form: ProfileForm;
  readonly protocolViolations: readonly string[];
}

export function profileToForm(profile: {
  gender?: string;
  goal?: string;
  age?: number;
  display_name?: string;
  headline?: string;
  bio?: string;
  city_or_region?: string;
  work_sphere?: string | null;
  education_sphere?: string | null;
  education_type?: string | null;
}): ProfileToFormResult {
  const violations: string[] = [];
  const result = {} as {
    -readonly [K in keyof ProfileForm]: ProfileForm[K];
  };
  if (profile.display_name !== undefined) result.display_name = profile.display_name;
  if (profile.gender === 'male' || profile.gender === 'female') {
    result.gender = profile.gender;
  }
  if (
    profile.goal === 'equal_seeker' ||
    profile.goal === 'wealthy_seeker' ||
    profile.goal === 'no_work' ||
    profile.goal === 'companion_seeker'
  ) {
    result.scenario = goalToScenario(profile.goal);
  }
  if (typeof profile.age === 'number' && Number.isFinite(profile.age)) {
    result.age = profile.age;
  }
  if (profile.city_or_region !== undefined) result.city = profile.city_or_region;
  if (profile.headline !== undefined) result.headline = profile.headline;
  if (profile.bio !== undefined) result.bio = profile.bio;

  // B-2.A: strict protocol normalization for the three optional fields.
  // Each helper returns `{ value, protocolViolations }`. We merge the
  // violations so the reducer can surface drift via serverError.
  const workSphere = normalizeWorkSphere(profile.work_sphere);
  if (workSphere.value !== undefined) result.work_sphere = workSphere.value;
  violations.push(...workSphere.protocolViolations);

  const educationSphere = normalizeEducationSphere(profile.education_sphere);
  if (educationSphere.value !== undefined) result.education_sphere = educationSphere.value;
  violations.push(...educationSphere.protocolViolations);

  const educationType = normalizeEducationType(profile.education_type);
  if (educationType.value !== undefined) result.education_type = educationType.value;
  violations.push(...educationType.protocolViolations);

  return { form: result, protocolViolations: violations };
}

/**
 * Convert `ProfileForm` to the backend `Update*ProfileRequest` shape
 * (without fingerprint and without `profile_type`; the caller adds
 * `profile_type` itself when calling the API helper, since each
 * helper accepts `Omit<…, 'profile_type'>`).
 *
 * B-2.A: the three optional work/education fields use the canonical
 * trichotomy that matches the backend's `*string` shape (see
 * `friend_profile_handlers.go` L72-83):
 *
 *   form.work_sphere === undefined → JSON key omitted   → no-op
 *   form.work_sphere === ''        → JSON key = ""       → clear (NULL)
 *   form.work_sphere === 'value'   → JSON key = 'value'  → set
 *
 * The "untouched" case (key omitted) is what makes
 * `PATCH`-style updates work: a user editing only `headline` does
 * not need to round-trip the optional fields. The "clear" case
 * is what makes the product-level clear operation work: the user
 * must actively express the clear intent on the wire so the
 * backend can distinguish "leave alone" from "explicitly clear".
 *
 * CORRECTION01: the previous implementation conflated these two
 * states — empty-string was treated as "omit", which made the
 * product-level clear operation silently fail. This version
 * preserves the empty string on the wire so the backend's
 * existing `*string == &"" → clear (NULL)` semantics fire.
 *
 * The `goal` union is the superset of FriendGoal | SponsorGoal —
 * the API helper accepts this via its wide request type and the
 * actual JSON body only carries the value that matches the
 * chosen profileType. See `effects.ts` for the call-site context.
 */
export function formToUpdatePayload(
  form: ProfileForm,
  _profileType: 'friend' | 'sponsor'
): {
  gender?: 'male' | 'female';
  goal?: 'equal_seeker' | 'wealthy_seeker' | 'companion_seeker' | 'no_work';
  age?: number;
  display_name?: string;
  headline?: string;
  bio?: string;
  city_or_region?: string;
  work_sphere?: string;
  education_sphere?: string;
  education_type?: string;
} {
  void _profileType;
  let goal: 'equal_seeker' | 'wealthy_seeker' | 'companion_seeker' | 'no_work' | undefined;
  if (form.scenario === 'equal') goal = 'equal_seeker';
  else if (form.scenario === 'sponsor') goal = 'wealthy_seeker';
  else if (form.scenario === 'no_work') goal = 'no_work';

  // B-2.A: the three optional fields use the canonical trichotomy
  // (untouched / clear / set) keyed on form-state:
  //   - undefined  → omit key (no-op)
  //   - ''         → emit key = '' (clear → NULL on server)
  //   - non-empty  → emit key = value (set)
  //
  // This matches the frozen backend contract §2.1 (*string
  // trichotomy) and lets the FE actually clear an optional field
  // via the "Не указано" option without also accidentally
  // touching unrelated optional fields. (CORRECTION01 fix for
  // FE wire behavior bug.)
  const payload: Record<string, unknown> = {
    gender: form.gender,
    goal,
    age: form.age,
    display_name: form.display_name,
    headline: form.headline,
    bio: form.bio,
    city_or_region: form.city,
  };
  if (form.work_sphere === '') {
    payload.work_sphere = '';
  } else if (typeof form.work_sphere === 'string') {
    payload.work_sphere = form.work_sphere;
  }
  if (form.education_sphere === '') {
    payload.education_sphere = '';
  } else if (typeof form.education_sphere === 'string') {
    payload.education_sphere = form.education_sphere;
  }
  if (form.education_type === '') {
    payload.education_type = '';
  } else if (typeof form.education_type === 'string') {
    payload.education_type = form.education_type;
  }

  return payload as ReturnType<typeof formToUpdatePayload>;
}

/** Predicate: did this transition just become active? */
function becameActive(
  prev: ProfileFormModel,
  nextStatus: 'inactive' | 'active' | 'blocked' | 'deleted'
): boolean {
  return prev.profileStatus !== 'active' && nextStatus === 'active';
}

/**
 * Convert a canonical server photo list into PhotoEntry[] in
 * 'persisted' form. The server returns ids / urls / positions
 * verbatim; the reducer does NOT fabricate any of these fields.
 *
 * The list is assumed to already be sorted by position ascending
 * (per backend contract §7); we preserve order.
 */
function photosToPersistedEntries(
  photos: readonly import('@/api/profilePhotos').ProfilePhoto[]
): PhotoEntry[] {
  return photos.map((p) => ({
    id: p.id,
    kind: 'persisted' as const,
    url: p.url,
    position: p.position,
  }));
}

/**
 * CORRECTION02 R6 — single-flight photo upload queue.
 *
 * The frozen FE contract (`b2-b-frontend-media-contract01.md`
 * MULTI_FILE_PICK) requires uploads to be sequential — one
 * network round-trip at a time, in user-selection order — so
 * that:
 *
 *   - the server-assigned canonical positions match user
 *     selection order (deterministic progress + ordering),
 *   - recovery is simpler (one in-flight request per profile),
 *   - max-six admission and BOLA authorization are easier to
 *     reason about.
 *
 * The previous implementation emitted N UploadPhoto effects
 * at once (cascade after SubmitSucceeded, multi-file selection
 * in EDIT mode), and the harness's `runEffect` loop did not
 * await one before starting the next. That was effectively
 * concurrent.
 *
 * This helper is the queue authority. It looks at the current
 * `transientPhotos` and:
 *
 *   - if any entry is already `uploading`, returns the model
 *     unchanged with no effect (queue occupied);
 *   - otherwise picks the FIRST eligible entry (oldest
 *     `new-local` first; selection order preserved via insertion
 *     order in `transientPhotos`) that has a `file` attached,
 *     flips it to `uploading`, and emits ONE UploadPhoto effect.
 *
 * `failed-upload` entries are NOT picked up by the queue;
 * they require an explicit retry (R2 PhotoUploadStarted
 * transitions the entry back to `new-local`, and the queue
 * then picks it up on the next settle). This implements the
 * "queue continues past failed uploads" failure policy
 * explicitly.
 *
 * Invariant maintained by every photo transition:
 *
 *   # of transient entries with kind === 'uploading' <= 1
 *
 * Q1 (cascade in CREATE): only the first eligible is started;
 * subsequent ones wait for the running upload to settle.
 * Q4 (EDIT first-attempt): kind becomes 'uploading' before
 * the effect is emitted.
 */
function startNextPendingPhotoUpload(model: ProfileFormModel): UpdateResult {
  const alreadyUploading = model.transientPhotos.some(
    (p) => p.kind === 'uploading'
  );
  if (alreadyUploading) {
    return { model, effects: [] };
  }
  const next = model.transientPhotos.find(
    (p) => p.kind === 'new-local' && p.file
  );
  if (!next || !next.file) {
    // No eligible entry (or the entry has no File). The model
    // passed in already carries the just-appended entry; we
    // recompute dirty so the PhotoSelected transition is
    // reflected even when no upload is started here.
    return {
      model: {
        ...model,
        dirty: recomputeDirty(model),
      },
      effects: [],
    };
  }
  const transientPhotos = model.transientPhotos.map((p) =>
    p.id === next.id ? { ...p, kind: 'uploading' as const } : p
  );
  return {
    model: {
      ...model,
      transientPhotos,
      dirty: recomputeDirty(model),
    },
    effects: [{ type: 'UploadPhoto', localId: next.id, file: next.file }],
  };
}

/**
 * Pure update function — the heart of the Elm-ish architecture.
 */
export function update(
  model: ProfileFormModel,
  msg: ProfileQuestionnaireMsg
): UpdateResult {
  switch (msg.type) {
    case 'Started':
      if (model.mode.kind === 'edit') {
        return {
          model: {
            ...model,
            status: 'loading',
            serverError: null,
            serverErrorCode: null,
          },
          // B-2.C: also kick off skills taxonomy + selection load.
          // These go through the same command mailbox and run in
          // dispatch order. LoadProfileSkills is gated on
          // hasProfile=true (the effect runner uses the existing
          // profile to derive the owner endpoint).
          effects: [
            { type: 'LoadExistingProfile' },
            { type: 'LoadSkillsTaxonomy' },
            { type: 'LoadProfileSkills' },
          ],
        };
      }
      return {
        model: {
          ...model,
          status: 'ready',
          serverError: null,
          serverErrorCode: null,
        },
        // B-2.C CREATE: load taxonomy only. No profile-skills GET
        // until the canonical profile exists.
        effects: [{ type: 'LoadSkillsTaxonomy' }],
      };

    case 'ExistingProfileLoaded': {
      // B-2.A: route the server-returned lifecycle through
      // `consumeLifecycleFromServer` so unknown profile_status values
      // are surfaced explicitly (collapsed to 'inactive' +
      // protocolViolations surfaced via serverError). The server
      // remains canonical; the FE refuses to derive activation.
      //
      // R1+R2 fix: also merge `formProtocolViolations` (unknown
      // work/education enum values from `profileToForm`) into the
      // same `serverError` so contract drift is visible, not silent.
      //
      // CORRECTION01 R4: also merge `photoProtocolViolations` from
      // the photo normalizer. The previous behavior silently dropped
      // malformed / role-prefixed / over-six / negative-position
      // photo entries; we now surface them as visible serverError.
      // The valid photos are still adopted (so the user can act),
      // but the protocol drift is loud, not silent.
      const lifecycle = consumeLifecycleFromServer({
        profile_status: msg.profileStatus,
        missing_activation_fields: msg.missingActivationFields,
      });
      const combinedViolations: readonly string[] = [
        ...lifecycle.protocolViolations,
        ...(msg.formProtocolViolations ?? []),
        ...(msg.photoProtocolViolations ?? []),
      ];
      const next: ProfileFormModel = {
        ...model,
        status: 'ready',
        form: msg.profile,
        original: msg.profile,
        persistedPhotos: msg.persistedPhotos,
        transientPhotos: [],
        validationErrors: [],
        missingActivationFields: lifecycle.missingActivationFields,
        profileStatus: lifecycle.profileStatus,
        dirty: false,
        hasProfile: true,
        serverError:
          combinedViolations.length > 0
            ? combinedViolations.join('; ')
            : null,
        serverErrorCode:
          combinedViolations.length > 0 ? 'validation_error' : null,
      };
      return { model: next, effects: [] };
    }

    case 'ExistingProfileLoadFailed':
      return {
        model: {
          ...model,
          status: 'error',
          serverError: msg.error,
          serverErrorCode: 'network_error',
        },
        effects: [],
      };

    case 'NameChanged':
    case 'HeadlineChanged':
    case 'BioChanged':
    case 'CityChanged':
    case 'WorkSphereChanged':
    case 'EducationSphereChanged':
    case 'EducationTypeChanged': {
      const field =
        msg.type === 'NameChanged' ? 'display_name'
        : msg.type === 'HeadlineChanged' ? 'headline'
        : msg.type === 'BioChanged' ? 'bio'
        : msg.type === 'CityChanged' ? 'city'
        : msg.type === 'WorkSphereChanged' ? 'work_sphere'
        : msg.type === 'EducationSphereChanged' ? 'education_sphere'
        : 'education_type';
      const value =
        msg.type === 'NameChanged' ? msg.value
        : msg.type === 'HeadlineChanged' ? msg.value
        : msg.type === 'BioChanged' ? msg.value
        : msg.type === 'CityChanged' ? msg.value
        : msg.type === 'WorkSphereChanged' ? msg.value
        : msg.type === 'EducationSphereChanged' ? msg.value
        : msg.value;
      const form: ProfileForm = { ...model.form, [field]: value };
      const errors = validateAllFields(form);
      return {
        model: {
          ...model,
          form,
          validationErrors: errors,
          dirty: recomputeDirty({ ...model, form }),
        },
        effects: [],
      };
    }

    case 'GenderChanged':
    case 'ScenarioChanged':
    case 'AgeChanged': {
      const field =
        msg.type === 'GenderChanged' ? 'gender'
        : msg.type === 'ScenarioChanged' ? 'scenario'
        : 'age';
      const value =
        msg.type === 'GenderChanged' ? msg.value
        : msg.type === 'ScenarioChanged' ? msg.value
        : msg.value;
      const form: ProfileForm = { ...model.form, [field]: value };
      const errors = validateAllFields(form);
      return {
        model: {
          ...model,
          form,
          validationErrors: errors,
          dirty: recomputeDirty({ ...model, form }),
        },
        effects: [],
      };
    }

    case 'PhotoSelected': {
      // The view has already allocated the object-URL preview via
      // the injected `ObjectUrlPort` (the URL is on `msg.entry.url`).
      // We append the entry.
      //
      // CORRECTION01 R1 — CREATE-mode upload sequencing:
      //   In CREATE mode, the canonical profile row does not exist
      //   yet on the server. The backend requires the canonical
      //   profile to own the photo row. Therefore we MUST NOT
      //   even consult the queue while `hasProfile=false` —
      //   uploads are deferred to SubmitSucceeded's cascade.
      //
      // CORRECTION02 R6 — single-flight queue:
      //   Once the R1 gate is cleared (EDIT mode, or CREATE after
      //   SubmitSucceeded), the upload decision is delegated to
      //   `startNextPendingPhotoUpload` which enforces "at most
      //   one uploading":
      //     - if the queue is empty → flip THIS entry to
      //       'uploading' and emit ONE UploadPhoto effect;
      //     - if the queue is occupied → append as `new-local`
      //       and emit NO effect (it will be picked up after the
      //       current upload settles).
      //   This handles both EDIT first-attempt (Q4) and
      //   multi-file selection cleanly without ever running two
      //   uploads concurrently.
      //
      // Revocation is handled by the reducer when the entry leaves
      // the model (success / cancel / delete / failed removal).
      const isCreateNoProfile =
        model.mode.kind === 'create' && !model.hasProfile;
      const withEntry: ProfileFormModel = {
        ...model,
        transientPhotos: [...model.transientPhotos, msg.entry],
      };
      if (isCreateNoProfile) {
        // R1: do not consult the queue; the canonical profile
        // doesn't exist yet. SubmitSucceeded will drive the
        // cascade. dirty still flips to true.
        return {
          model: {
            ...withEntry,
            dirty: recomputeDirty(withEntry),
          },
          effects: [],
        };
      }
      return startNextPendingPhotoUpload(withEntry);
    }

    case 'PhotoUploadStarted': {
      // CORRECTION01 R2: retry-from-failed-upload must actually
      // re-run the upload adapter. The previous behavior only
      // flipped `kind` to 'uploading' but emitted no UploadPhoto
      // effect, leaving the user in a spinner-forever state.
      //
      // CORRECTION02 R6: retry must NOT bypass the single-flight
      // queue. The clean way to honor both invariants is:
      //
      //   1. flip `failed-upload` → `new-local` (re-enqueue);
      //   2. delegate to startNextPendingPhotoUpload which:
      //        - if queue is empty, flips THIS entry to
      //          'uploading' and emits ONE UploadPhoto effect;
      //        - if another upload is in flight, leaves this
      //          entry as `new-local` and emits NO effect
      //          (the queue will pick it up when the running
      //          upload settles).
      //
      // Without this, retry could race the cascade / multi-file
      // selection and we'd lose the "at most one uploading"
      // invariant.
      const target = model.transientPhotos.find((p) => p.id === msg.id);
      if (!target || !target.file) {
        // No entry, or entry has no File (e.g. an entry from an
        // old session after a hard reload). Idempotent no-op; the
        // user sees the failed-upload affordance and must
        // re-select.
        return { model, effects: [] };
      }
      if (target.kind === 'uploading') {
        // Already in flight — no-op. The harness's existing run
        // is authoritative; the user must wait.
        return { model, effects: [] };
      }
      const requeued: ProfileFormModel = {
        ...model,
        transientPhotos: model.transientPhotos.map((p) =>
          p.id === target.id
            ? {
                ...p,
                kind: 'new-local' as const,
                errorMessage: undefined,
              }
            : p
        ),
      };
      return startNextPendingPhotoUpload(requeued);
    }

    case 'PhotoUploadSucceeded': {
      // B-2.B: the server response is canonical durability authority.
      // We adopt the server-returned photo list verbatim. The
      // pending preview entry is removed (its preview URL is revoked
      // via the effect). The server list may include new entries
      // (other concurrent uploads) — adopt the full list.
      //
      // CORRECTION02 R6 — single-flight queue: after a successful
      // settle, delegate to startNextPendingPhotoUpload which will
      // start the next queued entry if any. This is what
      // implements the "one at a time, in selection order"
      // contract.
      const pending = model.transientPhotos.find((p) => p.id === msg.id);
      const transientPhotos = model.transientPhotos.filter(
        (p) => p.id !== msg.id
      );
      const persistedPhotos = photosToPersistedEntries(msg.serverPhotos);
      const revokeEffects: ProfileQuestionnaireEffect[] = [];
      if (pending && pending.url && pending.url.startsWith('blob:')) {
        revokeEffects.push({
          type: 'RevokeObjectURLPreview',
          url: pending.url,
        });
      }
      const settled: ProfileFormModel = {
        ...model,
        persistedPhotos,
        transientPhotos,
        dirty: recomputeDirty(model),
      };
      const next = startNextPendingPhotoUpload(settled);
      return {
        model: next.model,
        effects: [...revokeEffects, ...next.effects],
      };
    }

    case 'PhotoUploadFailed': {
      // The upload failed. Leave the pending entry as `failed-upload`
      // so the user can retry or remove. If the server returned a
      // canonical photo list (race-resync; upload may have succeeded
      // server-side even though the client saw a network error),
      // adopt it for the durable state.
      //
      // CORRECTION02 R6 — failure policy:
      //   "queue continues past failed uploads" — the failed entry
      //   stays as `failed-upload` (the user can retry), and
      //   startNextPendingPhotoUpload picks the NEXT eligible
      //   new-local entry (skipping failed-upload entries).
      //   This is a deliberate, documented policy choice.
      const transientPhotos = model.transientPhotos.map((p) =>
        p.id === msg.id
          ? { ...p, kind: 'failed-upload' as const, errorMessage: msg.error }
          : p
      );
      const persistedPhotos = msg.serverPhotos
        ? photosToPersistedEntries(msg.serverPhotos)
        : model.persistedPhotos;
      const settled: ProfileFormModel = {
        ...model,
        persistedPhotos,
        transientPhotos,
        dirty: recomputeDirty(model),
      };
      return startNextPendingPhotoUpload(settled);
    }

    case 'PhotoDeleteRequested': {
      // Optimistic: mark the targeted durable photo as `deleting`
      // and emit the DeletePhoto effect. If the target is a pending
      // (non-durable) entry, drop it and revoke its preview URL.
      const target = model.persistedPhotos.find((p) => p.id === msg.id);
      let persistedPhotos = model.persistedPhotos;
      let transientPhotos = model.transientPhotos;
      const effects: ProfileQuestionnaireEffect[] = [];
      if (target) {
        persistedPhotos = model.persistedPhotos.map((p) =>
          p.id === msg.id ? { ...p, kind: 'deleting' as const } : p
        );
        // The actual `kind` (friend/sponsor) is supplied by the
        // program harness via its EffectContext, NOT here in the
        // pure reducer. The effect payload carries only the photo
        // UUID; the harness routes the call to the right role-aware
        // adapter.
        effects.push({
          type: 'DeletePhoto',
          photoID: msg.id,
        });
      } else {
        const t = model.transientPhotos.find((p) => p.id === msg.id);
        if (t) {
          transientPhotos = model.transientPhotos.filter(
            (p) => p.id !== msg.id
          );
          if (t.url && t.url.startsWith('blob:')) {
            effects.push({
              type: 'RevokeObjectURLPreview',
              url: t.url,
            });
          }
        }
      }
      return {
        model: {
          ...model,
          persistedPhotos,
          transientPhotos,
          dirty: recomputeDirty(model),
        },
        effects,
      };
    }

    case 'PhotoDeleteSucceeded': {
      // B-2.B: the server returns the canonical resequenced photo
      // list. Adopt it verbatim. The local `deleting` entry has
      // already been filtered out server-side.
      const persistedPhotos = photosToPersistedEntries(msg.serverPhotos);
      return {
        model: {
          ...model,
          persistedPhotos,
          dirty: recomputeDirty(model),
        },
        effects: [],
      };
    }

    case 'PhotoDeleteFailed': {
      // The delete failed (network, server, BOLA). Restore the
      // durable entry from `deleting` back to `persisted`. If the
      // server also returned a canonical photo list (race-resync),
      // adopt it; otherwise keep the current durable state.
      const persistedPhotos = msg.serverPhotos
        ? photosToPersistedEntries(msg.serverPhotos)
        : model.persistedPhotos.map((p) =>
            p.id === msg.id
              ? { ...p, kind: 'persisted' as const, errorMessage: msg.error }
              : p
          );
      return {
        model: {
          ...model,
          persistedPhotos,
          dirty: recomputeDirty(model),
        },
        effects: [],
      };
    }

    case 'PhotoPreviewReleased': {
      // The user explicitly released a preview (e.g. before
      // removing a failed-upload entry). Drop the pending entry
      // and revoke its preview URL.
      const target = model.transientPhotos.find((p) => p.id === msg.id);
      const transientPhotos = model.transientPhotos.filter(
        (p) => p.id !== msg.id
      );
      const effects: ProfileQuestionnaireEffect[] = [];
      if (target && target.url && target.url.startsWith('blob:')) {
        effects.push({
          type: 'RevokeObjectURLPreview',
          url: target.url,
        });
      }
      return {
        model: {
          ...model,
          transientPhotos,
          dirty: recomputeDirty(model),
        },
        effects,
      };
    }

    case 'SubmitRequested': {
      const errors = validateAllFields(model.form);
      if (errors.length > 0) {
        return {
          model: {
            ...model,
            validationErrors: errors,
            serverError: null,
            serverErrorCode: null,
          },
          effects: [],
        };
      }
      if (!model.dirty) {
        return { model, effects: [] };
      }
      return {
        model: {
          ...model,
          status: 'saving',
          serverError: null,
          serverErrorCode: null,
          validationErrors: [],
        },
        effects: [
          {
            type: 'SaveProfile',
            form: model.form,
            kind: model.mode.kind === 'edit' ? 'edit' : 'create',
          },
        ],
      };
    }

    case 'SubmitSucceeded': {
      // B-2.A: same lifecycle extraction as ExistingProfileLoaded.
      // The save result's profile_status is server-canonical; unknown
      // values collapse to 'inactive' and surface via serverError.
      //
      // R1+R2 fix: also merge `formProtocolViolations` so unknown
      // work/education values returned from the saved profile are
      // surfaced alongside lifecycle violations.
      //
      // CORRECTION01 R1 — CREATE-mode upload cascade:
      //   After a successful CREATE save, the canonical profile
      //   row now exists on the server. Any pending
      //   `new-local` photos that the user selected BEFORE the
      //   profile existed are now eligible to be uploaded.
      //
      // CORRECTION02 R6 — single-flight queue:
      //   The cascade is delegated to
      //   startNextPendingPhotoUpload which emits exactly ONE
      //   UploadPhoto effect (the first eligible entry, in
      //   selection order). The remaining entries are picked up
      //   one at a time as each upload settles.
      //
      //   EDIT-mode submits do NOT cascade (the user does not
      //   select photos while submitting a form edit).
      const lifecycle = consumeLifecycleFromServer({
        profile_status: msg.profileStatus,
        missing_activation_fields: msg.missingActivationFields,
      });
      const combinedViolations: readonly string[] = [
        ...lifecycle.protocolViolations,
        ...(msg.formProtocolViolations ?? []),
      ];
      const transitioned = becameActive(model, lifecycle.profileStatus);
      const isCreate = model.mode.kind === 'create';
      const justCompletedFirstTime = isCreate && transitioned;
      const becameCanonical =
        isCreate && !model.hasProfile; // first-time CREATE success
      const next: ProfileFormModel = {
        ...model,
        status: 'saved',
        form: msg.profile,
        original: msg.profile,
        missingActivationFields: lifecycle.missingActivationFields,
        profileStatus: lifecycle.profileStatus,
        hasProfile: true,
        serverError:
          combinedViolations.length > 0
            ? combinedViolations.join('; ')
            : null,
        serverErrorCode:
          combinedViolations.length > 0 ? 'validation_error' : null,
        dirty: false,
        completionModalOpen: justCompletedFirstTime,
        s005TransitionShown: justCompletedFirstTime,
        justCompletedFirstTime,
      };
      const cascadeEffects: ProfileQuestionnaireEffect[] = [];
      if (becameCanonical) {
        // CORRECTION02 R6 — single-flight queue:
        //   We do NOT iterate transientPhotos and emit N
        //   UploadPhoto effects. We delegate to
        //   startNextPendingPhotoUpload which enforces "at
        //   most one uploading" by starting exactly ONE
        //   eligible entry. Subsequent entries are picked up
        //   automatically as each upload settles
        //   (PhotoUploadSucceeded / PhotoUploadFailed call
        //   the same helper).
        //
        //   failed-upload entries are NOT picked up by the
        //   queue (the user must explicitly retry them).
        const cascaded = startNextPendingPhotoUpload(next);
        // B-2.C skills cascade: if the user picked skills before
        // the canonical profile existed (CREATE mode), persist them
        // AFTER the profile save succeeds. PREPROFILE_SKILLS_POST_COUNT
        // = 0 is preserved because this is the first skills POST in
        // the lifecycle.
        const skillsCascade: ProfileQuestionnaireEffect[] =
          next.selectedSkillIds.length > 0
            ? [
                {
                  type: 'SaveProfileSkills',
                  skillIds: [...next.selectedSkillIds],
                },
              ]
            : [];
        return {
          model: cascaded.model,
          effects: [...cascaded.effects, ...skillsCascade],
        };
      }
      // EDIT-mode success: persist skills if dirty.
      if (
        next.selectedSkillIds.length > 0 &&
        skillsListChanged(next.selectedSkillIds, next.persistedSkillIds)
      ) {
        cascadeEffects.push({
          type: 'SaveProfileSkills',
          skillIds: [...next.selectedSkillIds],
        });
      }
      return { model: next, effects: cascadeEffects };
    }

    case 'SubmitFailed':
      return {
        model: {
          ...model,
          status: 'ready',
          serverError: msg.error,
          serverErrorCode: msg.code ?? 'unknown',
        },
        effects: [],
      };

    case 'CompletionModalDismissed':
      return {
        model: { ...model, completionModalOpen: false },
        effects: [],
      };

    case 'RetryRequested':
      if (model.mode.kind === 'edit') {
        return {
          model: {
            ...model,
            status: 'loading',
            serverError: null,
            serverErrorCode: null,
          },
          effects: [{ type: 'LoadExistingProfile' }],
        };
      }
      return { model, effects: [] };

    case 'S005StartRequested':
      return {
        model: { ...model, s005TransitionShown: false },
        effects: [],
      };

    // ========================================================================
    // B-2.C skills / preferences cases
    // ========================================================================
    case 'SkillsTaxonomyLoadRequested': {
      return {
        model: {
          ...model,
          skillsTaxonomy: {
            status: 'loading',
            items:
              model.skillsTaxonomy.status === 'idle' ||
              model.skillsTaxonomy.status === 'failed'
                ? []
                : model.skillsTaxonomy.items,
            error: null,
          },
        },
        effects: [{ type: 'LoadSkillsTaxonomy' }],
      };
    }

    case 'SkillsTaxonomyLoaded': {
      const violations = msg.protocolViolations ?? [];
      const nextServerError =
        violations.length > 0
          ? `${model.serverError ?? ''}${
              model.serverError ? '; ' : ''
            }${violations.join('; ')}`.trim() || null
          : model.serverError;
      return {
        model: {
          ...model,
          skillsTaxonomy: {
            status: 'ready',
            items: msg.skills,
            error: violations.length > 0 ? violations.join('; ') : null,
          },
          serverError: nextServerError,
          // CORRECTION01 of the §17 cluster: do NOT overwrite an
          // existing non-validation classification (e.g.
          // `network_error`, `role_not_classified`) just because a
          // subsequent load produced a non-null serverError. Only
          // declare `validation_error` when this load introduced
          // the violation itself. Preserve the prior code when no
          // new violation was added.
          serverErrorCode:
            violations.length > 0
              ? 'validation_error'
              : model.serverErrorCode,
        },
        effects: [],
      };
    }

    case 'SkillsTaxonomyLoadFailed': {
      return {
        model: {
          ...model,
          skillsTaxonomy: {
            status: 'failed',
            items:
              model.skillsTaxonomy.status === 'ready'
                ? model.skillsTaxonomy.items
                : [],
            error: msg.error,
          },
        },
        effects: [],
      };
    }

    case 'ProfileSkillsLoadRequested': {
      return { model, effects: [{ type: 'LoadProfileSkills' }] };
    }

    case 'ProfileSkillsLoaded': {
      const violations = msg.protocolViolations ?? [];
      const ids = msg.skills.map((s) => s.id);
      const nextServerError =
        violations.length > 0
          ? `${model.serverError ?? ''}${
              model.serverError ? '; ' : ''
            }${violations.join('; ')}`.trim() || null
          : model.serverError;
      return {
        model: {
          ...model,
          persistedSkillIds: ids,
          selectedSkillIds: ids,
          skillsError: null,
          serverError: nextServerError,
          // CORRECTION01 of the §17 cluster: preserve prior
          // serverErrorCode (e.g. `network_error`,
          // `role_not_classified`) when this load adds no new
          // violation. Only stamp `validation_error` when this
          // load itself introduced protocol violations.
          serverErrorCode:
            violations.length > 0
              ? 'validation_error'
              : model.serverErrorCode,
        },
        effects: [],
      };
    }

    case 'ProfileSkillsLoadFailed': {
      return {
        model: { ...model, skillsError: msg.error },
        effects: [],
      };
    }

    case 'SkillSelected': {
      if (model.selectedSkillIds.includes(msg.skillId)) {
        return { model, effects: [] };
      }
      if (model.selectedSkillIds.length >= MAX_SELECTED_SKILLS) {
        return { model, effects: [] };
      }
      const selectedSkillIds = [...model.selectedSkillIds, msg.skillId];
      return {
        model: {
          ...model,
          selectedSkillIds,
          dirty: recomputeDirty({ ...model, selectedSkillIds }),
        },
        effects: [],
      };
    }

    case 'SkillRemoved': {
      if (!model.selectedSkillIds.includes(msg.skillId)) {
        return { model, effects: [] };
      }
      const selectedSkillIds = model.selectedSkillIds.filter(
        (id) => id !== msg.skillId
      );
      return {
        model: {
          ...model,
          selectedSkillIds,
          dirty: recomputeDirty({ ...model, selectedSkillIds }),
        },
        effects: [],
      };
    }

    case 'SkillsSearchChanged': {
      return {
        model: { ...model, skillsSearch: msg.value },
        effects: [],
      };
    }

    case 'SkillsCategoryChanged': {
      return {
        model: { ...model, skillsCategory: msg.value },
        effects: [],
      };
    }

    case 'ProfileSkillsSaveRequested': {
      return {
        model: { ...model, skillsSave: 'saving', skillsError: null },
        effects: [
          {
            type: 'SaveProfileSkills',
            skillIds: [...model.selectedSkillIds],
          },
        ],
      };
    }

    case 'ProfileSkillsSaveSucceeded': {
      const ids = msg.skills.map((s) => s.id);
      return {
        model: {
          ...model,
          skillsSave: 'idle',
          skillsError: null,
          selectedSkillIds: ids,
          persistedSkillIds: ids,
          skillsTaxonomy:
            model.skillsTaxonomy.status === 'ready'
              ? {
                  status: 'ready' as const,
                  items: mergeSkillsIntoTaxonomy(
                    model.skillsTaxonomy.items,
                    msg.skills
                  ),
                  error: model.skillsTaxonomy.error,
                }
              : { status: 'ready' as const, items: msg.skills, error: null },
        },
        effects: [],
      };
    }

    case 'ProfileSkillsSaveFailed': {
      return {
        model: {
          ...model,
          skillsSave: 'failed',
          skillsError: msg.error,
          selectedSkillIds: msg.retainedSelectedIds,
        },
        effects: [],
      };
    }

    default: {
      // Exhaustive: every union variant handled above.
      return { model, effects: [] };
    }
  }
}

/**
 * Merge new skill rows into the existing taxonomy items array.
 * Existing items keep their original ordering; new items are appended.
 */
function mergeSkillsIntoTaxonomy(
  existing: readonly import('@/api/skills').Skill[],
  added: readonly import('@/api/skills').Skill[]
): readonly import('@/api/skills').Skill[] {
  if (added.length === 0) return existing;
  const seen = new Set<string>(existing.map((s) => s.id));
  const merged = [...existing];
  for (const s of added) {
    if (!seen.has(s.id)) {
      seen.add(s.id);
      merged.push(s);
    }
  }
  return merged;
}

/**
 * True iff two ordered skill-ID lists differ in length or in any
 * position. Order matters (server canonical position).
 */
function skillsListChanged(
  a: readonly string[],
  b: readonly string[]
): boolean {
  if (a.length !== b.length) return true;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return true;
  }
  return false;
}